/**
 * Configuration centralisée pour l'onboarding
 * 
 * Ce fichier est la SOURCE DE VÉRITÉ pour tous les éléments de configuration
 * affichés dans le widget d'onboarding et pour la validation de la configuration.
 * 
 * @see docs/ONBOARDING_CONFIGURATION.md
 */

import { ConfigurationStatus } from '@/hooks/useConfigurationCheck';

/**
 * Mapping des onglets de la page Établissement
 * 
 * ⚠️ SYNCHRONISATION: Ces valeurs DOIVENT correspondre aux valeurs des TabsTrigger
 * dans src/pages/Establishment.tsx
 */
export const ESTABLISHMENT_TABS = {
  booking: '#booking',   // Lien de réservation, widget
  config: '#config',     // Mode de réservation, capacité, pause
  info: '#info',         // Nom, email, téléphone, adresse
  reviews: '#reviews',   // Lien d'avis Google
  data: '#data',         // Export, suppression
} as const;

export type EstablishmentTabHash = typeof ESTABLISHMENT_TABS[keyof typeof ESTABLISHMENT_TABS];

/**
 * Types pour les items de configuration
 */
export interface ConfigurationItem {
  /** Identifiant unique de l'item */
  id: string;
  
  /** Label affiché dans le widget */
  label: string;
  
  /** Description par défaut (quand non complété) */
  description: string;
  
  /** Description optionnelle quand complété. Utiliser {count} pour le compteur */
  descriptionComplete?: string;
  
  /** Nom de l'icône lucide-react (ex: 'Building', 'Clock') */
  icon: 'Building' | 'UtensilsCrossed' | 'Clock' | 'Rocket' | 'Star' | 'Bell' | 'Mail' | 'Phone' | 'MapPin' | 'Globe' | 'Image';
  
  /** Catégorie: critique (bloquant) ou recommandé */
  category: 'critical' | 'recommended';
  
  /** Chemin vers la page du dashboard */
  dashboardPath: string;
  
  /** Hash de l'onglet (si applicable) */
  tabHash?: EstablishmentTabHash;
  
  /** Numéro d'étape dans l'onboarding initial (1-6), undefined si pas dans l'onboarding */
  onboardingStep?: number;
  
  /** Clé de vérification dans ConfigurationStatus (boolean) */
  statusKey: keyof Pick<ConfigurationStatus, 
    'hasEstablishmentName' | 'hasActiveTables' | 'hasActiveTimeSlots' | 'isNotPaused' |
    'hasEmail' | 'hasPhone' | 'hasAddress' | 'hasGoogleMaps' | 'hasReviewLink' | 
    'hasNotificationEmail' | 'hasLogo'
  >;
  
  /** Clé optionnelle pour les compteurs dans ConfigurationStatus */
  countKey?: keyof Pick<ConfigurationStatus, 'activeTablesCount' | 'activeTimeSlotsCount'>;
}

/**
 * Configuration centralisée de tous les items
 * 
 * L'ordre dans ce tableau détermine l'ordre d'affichage dans le widget.
 */
export const CONFIGURATION_ITEMS: ConfigurationItem[] = [
  // ============================================
  // ITEMS CRITIQUES (bloquants pour la production)
  // ============================================
  {
    id: 'name',
    label: "Nom de l'établissement",
    description: 'Personnalisez le nom affiché aux clients',
    icon: 'Building',
    category: 'critical',
    dashboardPath: '/dashboard/establishment',
    tabHash: ESTABLISHMENT_TABS.info,
    statusKey: 'hasEstablishmentName',
  },
  {
    id: 'tables',
    label: 'Tables actives',
    description: 'Ajoutez au moins une table',
    descriptionComplete: '{count} table(s) configurée(s)',
    icon: 'UtensilsCrossed',
    category: 'critical',
    dashboardPath: '/dashboard/tables',
    onboardingStep: 2,
    statusKey: 'hasActiveTables',
    countKey: 'activeTablesCount',
  },
  {
    id: 'slots',
    label: 'Créneaux horaires',
    description: "Définissez vos horaires d'ouverture",
    descriptionComplete: '{count} créneau(x) actif(s)',
    icon: 'Clock',
    category: 'critical',
    dashboardPath: '/dashboard/time-slots',
    onboardingStep: 3,
    statusKey: 'hasActiveTimeSlots',
    countKey: 'activeTimeSlotsCount',
  },
  {
    id: 'paused',
    label: 'Réservations actives',
    description: 'Les réservations sont en pause !',
    descriptionComplete: 'Les clients peuvent réserver',
    icon: 'Rocket',
    category: 'critical',
    dashboardPath: '/dashboard/establishment',
    tabHash: ESTABLISHMENT_TABS.config,
    statusKey: 'isNotPaused',
  },
  
  // ============================================
  // ITEMS RECOMMANDÉS (non bloquants)
  // ============================================
  {
    id: 'review',
    label: "Lien d'avis Google",
    description: 'Collectez des avis après chaque visite',
    descriptionComplete: 'Configuré - Les emails de suivi sont activés',
    icon: 'Star',
    category: 'recommended',
    dashboardPath: '/dashboard/establishment',
    tabHash: ESTABLISHMENT_TABS.reviews,
    statusKey: 'hasReviewLink',
  },
  {
    id: 'notification',
    label: 'Email de notification',
    description: 'Recevez les alertes de réservation',
    icon: 'Bell',
    category: 'recommended',
    dashboardPath: '/dashboard/notifications',
    statusKey: 'hasNotificationEmail',
  },
  {
    id: 'email',
    label: 'Email de contact',
    description: 'Visible par vos clients',
    icon: 'Mail',
    category: 'recommended',
    dashboardPath: '/dashboard/establishment',
    tabHash: ESTABLISHMENT_TABS.info,
    statusKey: 'hasEmail',
  },
  {
    id: 'phone',
    label: 'Téléphone',
    description: 'Affiché dans les confirmations',
    icon: 'Phone',
    category: 'recommended',
    dashboardPath: '/dashboard/establishment',
    tabHash: ESTABLISHMENT_TABS.info,
    statusKey: 'hasPhone',
  },
];

// ============================================
// Fonctions utilitaires
// ============================================

/**
 * Récupère les items critiques
 */
export const getCriticalItems = (): ConfigurationItem[] => 
  CONFIGURATION_ITEMS.filter(item => item.category === 'critical');

/**
 * Récupère les items recommandés
 */
export const getRecommendedItems = (): ConfigurationItem[] => 
  CONFIGURATION_ITEMS.filter(item => item.category === 'recommended');

/**
 * Génère le lien complet pour un item de configuration
 */
export const getItemLink = (item: ConfigurationItem): string => 
  item.tabHash ? `${item.dashboardPath}${item.tabHash}` : item.dashboardPath;

/**
 * Génère la description appropriée pour un item selon son état
 */
export const getItemDescription = (
  item: ConfigurationItem, 
  isComplete: boolean, 
  count?: number
): string => {
  if (isComplete && item.descriptionComplete) {
    return item.descriptionComplete.replace('{count}', String(count ?? 0));
  }
  return item.description;
};

/**
 * Vérifie si un item est complété selon le status
 */
export const isItemComplete = (
  item: ConfigurationItem, 
  status: ConfigurationStatus
): boolean => {
  return Boolean(status[item.statusKey]);
};

/**
 * Récupère le compteur d'un item (si applicable)
 */
export const getItemCount = (
  item: ConfigurationItem, 
  status: ConfigurationStatus
): number | undefined => {
  if (!item.countKey) return undefined;
  return status[item.countKey] as number;
};
